import {
  ArrowDown,
  Check,
  ChevronsDownUp,
  Copy,
  Paperclip,
  Pencil,
  ShieldAlert,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { type DisplayMessage, messageText } from '../hooks/useChat';
import { useTypewriter } from '../hooks/useTypewriter';
import { useArtifactContext } from '../lib/artifactContext';
import {
  displayWorkspaceFileName,
  extractArtifactsForToolCall,
  extractBinaryArtifactsForToolCall,
  workspaceUploadId,
} from '../lib/artifacts';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';
import { MODEL_CATALOG } from '../lib/settings';
import type { ContentBlock, ToolCall } from '../lib/ws';
import { ArtifactCard } from './ArtifactCard';
import { MarkdownContent } from './MarkdownContent';
import { ThinkingBlock, ThinkingIndicator } from './ThinkingIndicator';
import { ToolCallBadge } from './ToolCallBadge';

// Row entry marks a new message without moving the reading surface.
const ROW_INITIAL = { opacity: 0, y: 6 };
const ROW_ANIMATE = { opacity: 1, y: 0 };
const ROW_TRANSITION = {
  duration: 0.22,
  ease: [0.16, 1, 0.3, 1],
} as const;
// User bubbles rise from the composer with a light spring, anchored
// at their bottom-right corner like a sent message.
const BUBBLE_INITIAL = { opacity: 0, y: 18, scale: 0.96 };
const BUBBLE_ANIMATE = { opacity: 1, y: 0, scale: 1 };
const BUBBLE_TRANSITION = { type: 'spring', stiffness: 420, damping: 34 } as const;
const BUBBLE_STYLE = { originX: 1, originY: 1 };

type Props = {
  messages: DisplayMessage[];
  isStreaming: boolean;
  compacting: boolean;
  streamingStartedAt: number | null;
  /** Called when the user wants to edit a sent user bubble. */
  onEditUserMessage?: (messageId: string, text: string, attachments?: string[]) => void;
};

/**
 * Message list. User bubbles right, assistant prose (no avatar, no
 * label per CLAUDE.md §8) left. Auto-scrolls to bottom whenever the
 * tail message grows. Assistant messages go through the markdown
 * renderer; user messages stay raw because they're typed input.
 */
export function ChatMessages({
  messages,
  isStreaming,
  compacting,
  streamingStartedAt,
  onEditUserMessage,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  // Latest non-slash user message may be edited and re-sent.
  let lastEditableUserId: string | null = null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m.role !== 'user') continue;
    const t = messageText(m).trim();
    if (/^\/[a-z][\w-]*(\s|$)/.test(t)) continue;
    lastEditableUserId = m.id;
    break;
  }
  // Drive the auto-scroll on the rough size of the tail message —
  // sum of every text block's length is a cheap proxy for "did
  // anything change". We don't include tool blocks because their
  // visual size is dominated by the result text the renderer
  // pulls from `toolCalls`, which changes through `tool_use_end`
  // and not via blocks.
  const tail = messages.at(-1);
  const tailLen = tail
    ? tail.blocks.reduce((n, b) => n + (b.type === 'text' ? b.text.length : 0), 0)
    : 0;
  const rafRef = useRef<number | null>(null);

  // "Follow mode": auto-scroll only sticks to the bottom while the
  // user IS at the bottom. The moment they scroll up to read earlier
  // content, we yield — chunks arriving from the stream stop yanking
  // them back. Re-anchor automatically when they scroll back down
  // within `STICK_THRESHOLD` of the bottom. `isFollowing` lives in
  // both a ref (read inside non-reactive code paths) and React state
  // (drives the "↓ jump to latest" pill).
  const STICK_THRESHOLD = 72;
  const followingRef = useRef(true);
  const [following, setFollowing] = useState(true);
  const setFollow = useCallback((next: boolean) => {
    followingRef.current = next;
    setFollowing((cur) => (cur === next ? cur : next));
  }, []);

  // Bind scroll listener once. Tracks whether the user is currently
  // at the bottom and toggles `following` so the auto-scroll effect
  // below can decide whether to chase the tail.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      setFollow(distance <= STICK_THRESHOLD);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    // Establish initial state in case the list mounts pre-scrolled.
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [setFollow]);

  // On a brand-new message (user submitted, or assistant_start fired)
  // we always re-anchor the user to the bottom regardless of where
  // they were — they explicitly produced content, the natural reading
  // position is the new tail. On tail growth only (streaming text /
  // tool deltas) we ONLY scroll if the user is still in follow mode.
  // The very first render-with-messages also forces a jump-to-bottom
  // so a session opens at the latest turn, not at history-top.
  const prevLenRef = useRef(messages.length);
  const firstEffectRef = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: tailLen retriggers scroll during stream
  useEffect(() => {
    const isNewMessage = messages.length !== prevLenRef.current;
    const wasFirstRun = firstEffectRef.current;
    prevLenRef.current = messages.length;
    firstEffectRef.current = false;
    if (wasFirstRun || isNewMessage) {
      // A new row's appearance (or the very first paint) counts as
      // user intent — re-engage follow mode so the rest of the
      // stream stays anchored.
      setFollow(true);
    } else if (!followingRef.current) {
      // Tail growing while user is reading earlier content — don't
      // pull them down. Bail before scheduling the scroll.
      return;
    }

    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      bottomRef.current?.scrollIntoView({
        // Instant scrolls during streaming so each token render
        // doesn't chase a 200 ms scroll animation that the next
        // token interrupts (jank on mobile). Smooth on idle —
        // visible only at the end-of-turn transition.
        behavior: isStreaming ? 'instant' : 'smooth',
        block: 'end',
      });
    });
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [messages.length, tailLen, isStreaming, compacting, setFollow]);

  const scrollToBottom = useCallback(() => {
    setFollow(true);
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [setFollow]);

  return (
    <div
      ref={scrollRef}
      className="mc-chat-scroll relative flex-1 overflow-y-auto overscroll-contain"
    >
      <div className="mx-auto w-full max-w-3xl px-4 pt-4 pb-6 md:px-8">
        {messages.length === 0 && !isStreaming && (
          <div className="mt-[min(18vh,7rem)]">
            <h2 className="min-w-0 font-display text-[28px] leading-tight [overflow-wrap:anywhere]">
              Начните с вопроса.
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Если для задачи нужен файл, прикрепите его.
            </p>
          </div>
        )}
        {messages.map((m) => (
          <MessageRow
            key={m.id}
            message={m}
            compacting={compacting && m.id === tail?.id}
            streamingStartedAt={streamingStartedAt}
            canEdit={
              !!onEditUserMessage &&
              !isStreaming &&
              m.role === 'user' &&
              m.id === lastEditableUserId
            }
            onEdit={onEditUserMessage}
          />
        ))}
        {/* No assistant row yet — server hasn't emitted `assistant_start`
         * but the user already sees their bubble echoed back. Drop a
         * standalone indicator so the lag isn't a silent void. */}
        {isStreaming && compacting && (
          <div
            className="my-3 inline-flex items-center gap-2 text-[13px] text-muted-foreground"
            aria-hidden="true"
          >
            <ChevronsDownUp size={14} />
            <span>Сжимаю контекст…</span>
          </div>
        )}
        {isStreaming && !compacting && messages.at(-1)?.role !== 'assistant' && (
          <div className="my-2">
            <ThinkingIndicator startedAt={streamingStartedAt} />
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      {/* "Jump to latest" pill. Visible when the user is reading
       * earlier content while the stream keeps producing — gives
       * them a single tap to re-engage follow mode. Sits inside the
       * scroll container so it floats above messages, not above the
       * composer. */}
      <AnimatePresence>
        {!following && (
          <motion.button
            key="jump"
            type="button"
            onClick={scrollToBottom}
            initial={{ opacity: 0, y: 8, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.92 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="sticky bottom-3 left-1/2 z-10 -translate-x-1/2 inline-flex min-h-11 items-center gap-1.5 whitespace-nowrap mc-glass mc-float rounded-full border border-border/70 bg-surface px-3 text-[12px] text-foreground/80 hover:bg-subtle"
          >
            <ArrowDown size={13} />
            <span>{isStreaming ? 'К новому' : 'Вниз'}</span>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

const MessageRow = memo(function MessageRow({
  message,
  compacting,
  streamingStartedAt,
  canEdit,
  onEdit,
}: {
  message: DisplayMessage;
  compacting: boolean;
  streamingStartedAt: number | null;
  canEdit?: boolean;
  onEdit?: (messageId: string, text: string, attachments?: string[]) => void;
}) {
  const { user } = useAuth();
  const username = user?.username ?? '';
  if (message.role === 'user') {
    // A slash command (e.g. `/compact`) the user invoked — render as a
    // compact mono chip rather than a chat bubble, so it reads as "ran a
    // command" not "said this sentence". Matches both the live echo and
    // the persisted row (same plain text on both paths).
    const userText = messageText(message).trim();
    if (/^\/[a-z][\w-]*(\s|$)/.test(userText)) {
      return (
        <motion.div
          initial={ROW_INITIAL}
          animate={ROW_ANIMATE}
          transition={ROW_TRANSITION}
          className="mb-4 flex justify-end"
        >
          <code className="max-w-[88%] truncate rounded-lg border border-border bg-subtle px-2.5 py-1 font-mono text-[13px] text-muted-foreground md:max-w-[78%]">
            {userText}
          </code>
        </motion.div>
      );
    }
    return (
      <motion.div
        initial={BUBBLE_INITIAL}
        animate={BUBBLE_ANIMATE}
        transition={BUBBLE_TRANSITION}
        style={BUBBLE_STYLE}
        className="group mb-4 flex justify-end gap-1.5"
      >
        {canEdit && onEdit && (
          <button
            type="button"
            onClick={() => onEdit(message.id, userText, message.attachments)}
            className={cn(
              'mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
              'text-muted-foreground/70',
              'opacity-100 md:opacity-0 md:group-hover:opacity-100',
              'hover:bg-subtle hover:text-foreground',
            )}
            title="Изменить сообщение"
            aria-label="Изменить сообщение"
          >
            <Pencil size={13} />
          </button>
        )}
        <div className="max-w-[85%] rounded-[22px] bg-bubble px-4 py-2.5 text-[16px] leading-snug md:max-w-[75%]">
          <div className="whitespace-pre-wrap break-words">{messageText(message)}</div>
          {message.attachments && message.attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {message.attachments.map((path, i) => {
                const name = displayWorkspaceFileName(path);
                const uploadId = workspaceUploadId(path);
                return (
                  <a
                    // biome-ignore lint/suspicious/noArrayIndexKey: an immutable message may attach the same path twice
                    key={`${path}:${i}`}
                    href={`/api/files/download?path=${encodeURIComponent(path)}`}
                    className="inline-flex max-w-full items-center gap-1 rounded-lg border border-border-strong px-2 py-1 text-[12px] underline-offset-2 hover:underline"
                    title={path}
                    aria-label={`Скачать ${name}${uploadId ? ` (${uploadId})` : ''}`}
                  >
                    <Paperclip size={12} className="shrink-0" aria-hidden="true" />
                    <span className="truncate">{name}</span>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  if (message.role === 'system') {
    return (
      <motion.div
        initial={ROW_INITIAL}
        animate={ROW_ANIMATE}
        transition={ROW_TRANSITION}
        className="mb-4 rounded-lg border border-border bg-subtle px-3 py-2 text-[13px] text-muted-foreground"
      >
        {messageText(message)}
      </motion.div>
    );
  }

  const hasThinking = !!message.thinking && message.thinking.length > 0;
  const toolCalls = message.toolCalls ?? [];
  const toolById = new Map<string, ToolCall>();
  for (const tc of toolCalls) toolById.set(tc.id, tc);

  const blocks = message.blocks;
  const hasAnyContent = blocks.some(
    (b) =>
      (b.type === 'text' && b.text.length > 0) ||
      b.type === 'tool_use' ||
      b.type === 'compact' ||
      b.type === 'model_fallback',
  );
  // Where to put the streaming caret: at the end of the last text
  // block that's actually growing. If the last visible block is a
  // tool_use, no caret (the tool has its own spinner state).
  const lastTextBlockIdx = (() => {
    for (let i = blocks.length - 1; i >= 0; i -= 1) {
      const b = blocks[i];
      if (b.type === 'text' && b.text.length > 0) return i;
    }
    return -1;
  })();
  const { activeId, onOpen } = useArtifactContext();

  return (
    <motion.div
      initial={ROW_INITIAL}
      animate={ROW_ANIMATE}
      transition={ROW_TRANSITION}
      className="mb-6"
    >
      {hasThinking && (
        <ThinkingBlock
          text={message.thinking ?? ''}
          // Reasoning can keep arriving after the first tool_use or
          // text block (thinking_delta mid-tool loop). Stay in the
          // live "Reasoning" state for the whole turn.
          streaming={!!message.streaming}
          // `startedAt` is only meaningful while this row is the
          // active streaming turn. History rows never streamed in
          // this session, so we don't know their reasoning duration
          // and shouldn't fake one. The block captures finalElapsed
          // internally when streaming→false transitions within the
          // component's lifetime.
          startedAt={message.streaming ? streamingStartedAt : null}
        />
      )}
      {blocks.map((b, i) => {
        if (b.type === 'text') {
          if (b.text.length === 0) return null;
          // Stable key derived from message+index: blocks never
          // reorder during a stream, only grow in count, so the
          // index is a fine identity. (The message id is added so
          // React doesn't reuse a row from a different message
          // when the user navigates between sessions.)
          const showCaret = !!message.streaming && i === lastTextBlockIdx;
          return (
            <TextBlock
              key={`${message.id}:b${i}`}
              text={b.text}
              messageId={message.id}
              isStreaming={!!message.streaming}
              showCaret={showCaret}
            />
          );
        }
        if (b.type === 'compact') {
          // Context-compaction boundary — full-width divider, sits in
          // source order between the prose before and after the squash.
          return <CompactDivider key={`${message.id}:b${i}`} block={b} />;
        }
        if (b.type === 'model_fallback') {
          // Safety-refusal model swap (Fable → Opus) — full-width
          // divider in source order, so the user sees exactly where
          // the conversation changed models mid-turn.
          return <FallbackDivider key={`${message.id}:b${i}`} block={b} />;
        }
        // tool_use block — render the badge and any artifacts the
        // tool produced inline, right under it. This keeps the
        // user-visible order exactly what the model did:
        //   text → tool (+ its artifact card) → text → tool …
        const tc = toolById.get(b.toolCallId);
        if (!tc) return null;
        const artifacts = [
          ...extractArtifactsForToolCall(tc, message.id, username),
          ...extractBinaryArtifactsForToolCall(tc, message.id, username),
        ];
        return (
          <div key={`${message.id}:b${i}`} className="my-2">
            <ToolCallBadge call={tc} streaming={!!message.streaming} />
            {artifacts.map((a) => (
              <ArtifactCard key={a.id} artifact={a} onOpen={onOpen} isOpen={activeId === a.id} />
            ))}
          </div>
        );
      })}
      {/* Streaming but nothing yet — neither a tool, text, nor a
       * reasoning trace. The animated indicator gives the user
       * confidence that something is happening rather than the row
       * appearing blank for 3-10 seconds while the model spins up
       * (long for opus-4-8 1m + effort max). */}
      {message.streaming && !compacting && !hasAnyContent && !hasThinking && (
        <ThinkingIndicator startedAt={streamingStartedAt} />
      )}
      {!message.streaming && <CopyAction text={messageText(message)} />}
    </motion.div>
  );
});

/** Action row under a finished answer: copy the whole reply as text. */
function CopyAction({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(id);
  }, [copied]);
  if (!text.trim()) return null;
  return (
    <div className="mt-1 -ml-2 flex">
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(text).then(() => setCopied(true));
        }}
        className="mc-press inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground active:bg-subtle md:hover:bg-subtle md:hover:text-foreground"
        title={copied ? 'Скопировано' : 'Копировать ответ'}
        aria-label={copied ? 'Скопировано' : 'Копировать ответ'}
      >
        {copied ? (
          <Check size={16} strokeWidth={2} className="mc-pop text-primary" />
        ) : (
          <Copy size={16} strokeWidth={1.8} />
        )}
      </button>
    </div>
  );
}

// Single assistant text block. Split out from the row body so the
// `useTypewriter` hook has a stable per-block instance — the hook
// count is fixed per render (one), and each block keeps its own
// reveal cursor independently. Memoised so a streaming tail block
// growing every 28 ms doesn't force re-renders of the earlier
// already-typed blocks above.
const TextBlock = memo(function TextBlock({
  text,
  messageId,
  isStreaming,
  showCaret,
}: {
  text: string;
  messageId: string;
  isStreaming: boolean;
  showCaret: boolean;
}) {
  const typed = useTypewriter(text, { isStreaming });
  // While the typewriter is still catching up, suppress the trailing
  // caret on the assistant's existing pulse — the bottom of the
  // typed region IS the caret position, and a second pulse below the
  // final typed char read as visual noise.
  const stillTyping = isStreaming && typed.length < text.length;
  return (
    <div>
      <MarkdownContent messageId={messageId}>{typed}</MarkdownContent>
      {showCaret && !stillTyping && (
        <span className="ml-1 inline-block h-4 w-1.5 translate-y-0.5 animate-pulse bg-foreground align-middle" />
      )}
    </div>
  );
});

function formatTokens(n: number): string {
  return n >= 1000 ? `${Math.round(n / 1000)}k` : String(n);
}

/**
 * Full-width "context compacted" divider. Marks where the CLI
 * squashed the conversation to free tokens — claude.ai parity. Quiet
 * by design (§8): a centered hairline + muted label, plus the token
 * delta (e.g. 154k → 42k) when the CLI reported both sides.
 */
const CompactDivider = memo(function CompactDivider({
  block,
}: {
  block: Extract<ContentBlock, { type: 'compact' }>;
}) {
  const detail =
    typeof block.preTokens === 'number' && typeof block.postTokens === 'number'
      ? `${formatTokens(block.preTokens)} → ${formatTokens(block.postTokens)}`
      : null;
  return (
    <motion.div
      initial={ROW_INITIAL}
      animate={ROW_ANIMATE}
      transition={ROW_TRANSITION}
      className="my-5 flex items-center gap-3 text-muted-foreground"
    >
      <span aria-hidden="true" className="h-px flex-1 bg-border" />
      <span className="inline-flex items-center gap-1.5 text-[12px] leading-none">
        <ChevronsDownUp size={13} className="opacity-70" />
        <span>Контекст сжат</span>
        {detail && <span className="text-[11px] tabular-nums opacity-70">· {detail}</span>}
      </span>
      <span aria-hidden="true" className="h-px flex-1 bg-border" />
    </motion.div>
  );
});

/** Friendly label for a model alias (Fable 5 / Opus 4.8 · 1M / …).
 * Falls back to the raw id if the server ever names a model this
 * client's catalog doesn't know. */
function modelLabel(alias: string): string {
  return MODEL_CATALOG.find((m) => m.id === alias)?.label ?? alias;
}

/**
 * Full-width "model switched" divider. Marks where Fable's safety
 * filter refused the turn and the CLI retried on Opus — the user
 * needs to see the swap, and that subsequent turns now run on the
 * fallback model. Quiet styling matches CompactDivider (§8): centered
 * hairline + muted label + shield glyph.
 */
const FallbackDivider = memo(function FallbackDivider({
  block,
}: {
  block: Extract<ContentBlock, { type: 'model_fallback' }>;
}) {
  const from = modelLabel(block.fromModel);
  const to = modelLabel(block.toModel);
  return (
    <motion.div
      initial={ROW_INITIAL}
      animate={ROW_ANIMATE}
      transition={ROW_TRANSITION}
      className="my-5 flex items-center gap-3 text-muted-foreground"
    >
      <span aria-hidden="true" className="h-px flex-1 bg-border" />
      <span className="inline-flex items-center gap-1.5 text-center text-[12px] leading-none">
        <ShieldAlert size={13} className="opacity-70" />
        <span>
          Фильтр безопасности {from} · переключено на {to}
        </span>
      </span>
      <span aria-hidden="true" className="h-px flex-1 bg-border" />
    </motion.div>
  );
});
